import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { publicAPI } from '../lib/publicAPI';
import { UserPlus, CheckCircle, AlertCircle } from 'lucide-react';

const employeeSchema = z.object({
  name: z.string().min(2, 'Name required'),
  email: z.string().email('Valid email required'),
  phone: z.string().min(10, 'Valid phone required'),
  tfn: z.string().regex(/^\d{9}$/, 'TFN must be 9 digits').optional().or(z.literal('')),
  super_fund_name: z.string().optional(),
  super_fund_abn: z.string().regex(/^\d{11}$/, 'ABN must be 11 digits').optional().or(z.literal('')),
  super_fund_member_number: z.string().optional(),
  bank_account_name: z.string().optional(),
  bank_bsb: z.string().regex(/^\d{6}$/, 'BSB must be 6 digits').optional().or(z.literal('')),
  bank_account_number: z.string().optional(),
});

export default function EmployeeOnboarding() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(employeeSchema),
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await publicAPI.validateInvite(token);
      setInviteData(response.data);
      setInviteValid(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await publicAPI.submitEmployeeOnboarding(token, data);
      setSubmitted(true);
      toast.success('Onboarding completed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">Invalid Invite</CardTitle>
            <CardDescription className="text-base mt-4">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground text-center">
              Please contact your employer for a new invite link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Onboarding Complete!
            </CardTitle>
            <CardDescription className="text-base mt-4">
              Thank you for completing your employee onboarding.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <p className="text-sm text-foreground/90">
                Your details have been submitted to <strong>{inviteData?.client_name}</strong> and {inviteData?.practice_name || 'their accountant'}.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                You'll be contacted if any additional information is needed.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
              <UserPlus className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Employee Onboarding
          </h1>
          <p className="text-muted-foreground">Complete your details for {inviteData?.client_name}</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Your Details</CardTitle>
            <CardDescription>Please provide your employment and tax information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Personal Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    {...register('name')}
                    defaultValue={inviteData?.employee_name || ''}
                  />
                  {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    defaultValue={inviteData?.employee_email || ''}
                  />
                  {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="0400 000 000"
                    {...register('phone')}
                  />
                  {errors.phone && <p className="text-sm text-red-500">{errors.phone.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tfn">Tax File Number (TFN)</Label>
                  <Input
                    id="tfn"
                    placeholder="123456789"
                    {...register('tfn')}
                    maxLength={9}
                    className="font-mono"
                  />
                  {errors.tfn && <p className="text-sm text-red-500">{errors.tfn.message}</p>}
                  <p className="text-xs text-muted-foreground">9 digits, no spaces</p>
                </div>
              </div>

              <hr />

              {/* Superannuation */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Superannuation Details</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="super_fund_name">Super Fund Name</Label>
                  <Input
                    id="super_fund_name"
                    placeholder="Australian Super"
                    {...register('super_fund_name')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="super_fund_abn">Super Fund ABN</Label>
                    <Input
                      id="super_fund_abn"
                      placeholder="12345678901"
                      {...register('super_fund_abn')}
                      maxLength={11}
                      className="font-mono"
                    />
                    {errors.super_fund_abn && <p className="text-sm text-red-500">{errors.super_fund_abn.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="super_fund_member_number">Member Number</Label>
                    <Input
                      id="super_fund_member_number"
                      placeholder="12345678"
                      {...register('super_fund_member_number')}
                    />
                  </div>
                </div>
              </div>

              <hr />

              {/* Bank Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Bank Account Details</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="bank_account_name">Account Name</Label>
                  <Input
                    id="bank_account_name"
                    placeholder="John Smith"
                    {...register('bank_account_name')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_bsb">BSB</Label>
                    <Input
                      id="bank_bsb"
                      placeholder="123456"
                      {...register('bank_bsb')}
                      maxLength={6}
                      className="font-mono"
                    />
                    {errors.bank_bsb && <p className="text-sm text-red-500">{errors.bank_bsb.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bank_account_number">Account Number</Label>
                    <Input
                      id="bank_account_number"
                      placeholder="12345678"
                      {...register('bank_account_number')}
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={submitting}
                data-testid="submit-employee-onboarding"
              >
                {submitting ? 'Submitting...' : 'Complete Onboarding'}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Your information is securely encrypted and only shared with your employer and {inviteData?.practice_name || 'their accountant'}.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
